/**
 * Modal: Importar agenda do Booking.com / Airbnb / Vrbo
 * ──────────────────────────────────────────────────────
 * Step 1 — host picks apartment + source + pastes the iCal URL.
 * Step 2 — preview shows future check-out dates as candidate cleaning
 *          dates; host can deselect any, tweak the default time, then
 *          confirm import.
 *
 * Backend dedupes by `ical_uid` so re-importing the same calendar is safe
 * — only new dates are created.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, Download, ExternalLink, CheckCircle2, AlertTriangle, Calendar, Loader2,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";

const SOURCES = [
  { id: "booking", label: "Booking.com",
    help: "Painel anfitrião → Calendário → Exportar calendário → copie o URL .ics." },
  { id: "airbnb", label: "Airbnb",
    help: "Calendário → Disponibilidade → Sincronizar calendários → Exportar calendário." },
  { id: "vrbo", label: "Vrbo / HomeAway",
    help: "Cal & Reservas → Importar/Exportar → Exportar calendário (.ics)." },
  { id: "other", label: "Outra plataforma (.ics URL)",
    help: "Cole o URL público .ics fornecido pela plataforma." },
];

const TIME_SLOTS = [
  "08:00", "09:00", "10:00", "11:00", "12:00", "13:00",
  "14:00", "15:00", "16:00", "17:00",
];

export default function IcalImportModal({ open, onClose, onSuccess }) {
  const [apartments, setApartments] = useState([]);
  const [apartmentId, setApartmentId] = useState("");
  const [source, setSource] = useState("booking");
  const [icalUrl, setIcalUrl] = useState("");
  const [defaultTime, setDefaultTime] = useState("11:00");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // {rows, ...}
  const [selected, setSelected] = useState(new Set()); // ical_uid set
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/apartments")
      .then(({ data }) => setApartments(data.items || []))
      .catch(() => {});
    setApartmentId("");
    setSource("booking");
    setIcalUrl("");
    setDefaultTime("11:00");
    setPreview(null);
    setSelected(new Set());
  }, [open]);

  const sourceMeta = useMemo(() => SOURCES.find(s => s.id === source) || SOURCES[0], [source]);

  async function fetchPreview() {
    if (!apartmentId) return toast.error("Escolha um alojamento.");
    if (!icalUrl) return toast.error("Cole o URL .ics da plataforma.");
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/bookings/ical/preview", {
        apartment_id: apartmentId,
        ical_url: icalUrl.trim(),
        source,
      });
      setPreview(data);
      // Pre-select all rows that aren't already imported.
      const next = new Set(
        (data.rows || []).filter(r => !r.already_imported).map(r => r.ical_uid),
      );
      setSelected(next);
      if (data.new_events === 0) {
        toast.info(
          data.future_events === 0
            ? "Nenhum check-out futuro neste calendário."
            : "Tudo já está importado — sem novidades.",
        );
      } else {
        toast.success(`${data.new_events} nova(s) data(s) prontas para importar.`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  function toggleRow(uid) {
    setSelected(s => {
      const next = new Set(s);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  }

  async function doImport() {
    if (!preview) return;
    const chosen = (preview.rows || []).filter(
      r => selected.has(r.ical_uid) && !r.already_imported,
    );
    if (chosen.length === 0) return toast.error("Selecione pelo menos uma data.");
    setImporting(true);
    try {
      const { data } = await api.post("/admin/bookings/ical/import", {
        apartment_id: apartmentId,
        source,
        rows: chosen.map(r => ({
          cleaning_date: r.cleaning_date,
          cleaning_time: defaultTime,
          apt_type: r.apt_type,
          ical_uid: r.ical_uid,
          notes: r.summary || undefined,
          same_day_turnover: !!r.same_day_turnover,
          must_finish_by: r.same_day_turnover ? "14:00" : undefined,
        })),
      });
      const msg = `${data.ok_count} criado(s)` + (data.fail_count ? `, ${data.fail_count} com erro` : "")
                + (data.skipped_count ? `, ${data.skipped_count} ignorado(s)` : "") + ".";
      if (data.fail_count > 0) toast.warning(msg);
      else toast.success(msg);
      if (data.ok_count > 0) {
        if (onSuccess) onSuccess(data);
        setTimeout(() => onClose && onClose(), 900);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  }

  if (!open) return null;

  const newRows = (preview?.rows || []).filter(r => !r.already_imported);
  const dupRows = (preview?.rows || []).filter(r => r.already_imported);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
         data-testid="ical-import-modal">
      <div className="lux-card w-full md:max-w-3xl max-h-[95vh] flex flex-col bg-[#041f1b] border-[#d4af37]/30">
        <div className="px-6 py-4 border-b border-[#1a4640] flex items-baseline justify-between gap-3">
          <div>
            <div className="eyebrow text-[#d4af37] flex items-center gap-2">
              <Download size={12} /> Importar agenda OTA
            </div>
            <h2 className="font-display text-2xl mt-1">
              Booking.com · Airbnb · Vrbo · iCal
            </h2>
            <p className="text-xs text-[#a0b2af] mt-1">
              Cole o URL do calendário e nós criamos as limpezas para os dias de check-out.
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-[#a0b2af] hover:text-white"
                  data-testid="ical-close">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Step 1 — inputs */}
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Alojamento</label>
              <select value={apartmentId}
                      onChange={e => setApartmentId(e.target.value)}
                      className="w-full mt-1 bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-3 py-2 text-sm outline-none"
                      data-testid="ical-apartment">
                <option value="">— Escolher alojamento —</option>
                {apartments.map(a => (
                  <option key={a.id} value={a.id}>{a.name} · {a.apt_type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Plataforma</label>
              <select value={source}
                      onChange={e => setSource(e.target.value)}
                      className="w-full mt-1 bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-3 py-2 text-sm outline-none"
                      data-testid="ical-source">
                {SOURCES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#a0b2af] flex items-center gap-2">
              URL do calendário (.ics)
              <ExternalLink size={10} />
            </label>
            <input type="url"
                   value={icalUrl}
                   onChange={e => setIcalUrl(e.target.value)}
                   placeholder="https://www.airbnb.com/calendar/ical/...ics"
                   className="w-full mt-1 bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-3 py-2 text-sm outline-none font-mono"
                   data-testid="ical-url" />
            <p className="text-[11px] text-[#a0b2af] mt-1">{sourceMeta.help}</p>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={fetchPreview} disabled={loading || !apartmentId || !icalUrl}
                    className="btn-gold px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="ical-preview-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {loading ? "A descarregar…" : "Pré-visualizar datas"}
            </button>
            <span className="text-[11px] text-[#a0b2af]">
              Não cria nada ainda — só lê o calendário e mostra o que existe.
            </span>
          </div>

          {/* Step 2 — preview table */}
          {preview && (
            <div className="space-y-3" data-testid="ical-preview">
              <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                  <div className="eyebrow text-[#d4af37]">Pré-visualização · {preview.source_label}</div>
                  <div className="text-sm text-[#fdfbf7]">
                    {preview.total_events} evento{preview.total_events !== 1 ? "s" : ""} no calendário ·
                    <span className="text-[#10b981] ml-1">{preview.new_events} novo{preview.new_events !== 1 ? "s" : ""}</span>
                    {preview.turnovers > 0 && (
                      <span className="text-[#fca5a5] ml-1" data-testid="ical-preview-turnovers">
                        · 🔥 {preview.turnovers} turnover{preview.turnovers !== 1 ? "s" : ""} mesmo dia
                      </span>
                    )}
                    {dupRows.length > 0 && (
                      <span className="text-[#a0b2af] ml-1">· {dupRows.length} já importado{dupRows.length !== 1 ? "s" : ""}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Hora padrão</label>
                  <select value={defaultTime}
                          onChange={e => setDefaultTime(e.target.value)}
                          className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1 text-xs outline-none"
                          data-testid="ical-default-time">
                    {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {newRows.length > 0 && (
                <div className="lux-card border-[#10b981]/30 overflow-hidden">
                  <div className="px-3 py-2 bg-[#10b981]/5 text-xs uppercase tracking-widest text-[#10b981] flex items-center gap-2">
                    <CheckCircle2 size={12} /> Novas ({selected.size}/{newRows.length} selecionadas)
                  </div>
                  <div className="divide-y divide-[#1a4640] max-h-72 overflow-y-auto">
                    {newRows.map(r => {
                      const isOn = selected.has(r.ical_uid);
                      return (
                        <label key={r.ical_uid}
                               className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#0a3a33] ${isOn ? "" : "opacity-60"} ${r.same_day_turnover ? "bg-[#ef4444]/5" : ""}`}
                               data-testid={`ical-row-${r.ical_uid}`}>
                          <input type="checkbox"
                                 checked={isOn}
                                 onChange={() => toggleRow(r.ical_uid)}
                                 className="w-4 h-4 accent-[#d4af37]" />
                          <Calendar size={14} className="text-[#d4af37] shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-[#fdfbf7] flex items-center gap-2 flex-wrap">
                              <strong>{r.cleaning_date}</strong> · {defaultTime}
                              <span className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{r.apt_type}</span>
                              {r.same_day_turnover && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#ef4444]/15 border border-[#ef4444]/40 text-[10px] uppercase tracking-widest text-[#fca5a5]"
                                      data-testid={`ical-row-turnover-${r.ical_uid}`}
                                      title="Próximo hóspede chega no mesmo dia — limpeza apertada">
                                  🔥 Turnover
                                </span>
                              )}
                            </div>
                            {r.summary && (
                              <div className="text-[11px] text-[#a0b2af] truncate">{r.summary}</div>
                            )}
                          </div>
                          {r.guest_checkin && (
                            <div className="text-[10px] text-[#a0b2af] hidden sm:block">
                              entrada: {r.guest_checkin}
                            </div>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {dupRows.length > 0 && (
                <div className="lux-card border-[#1a4640]">
                  <div className="px-3 py-2 text-xs uppercase tracking-widest text-[#a0b2af] flex items-center gap-2">
                    <AlertTriangle size={12} /> Já importadas — ignoradas
                  </div>
                  <div className="divide-y divide-[#1a4640] max-h-32 overflow-y-auto">
                    {dupRows.map(r => (
                      <div key={r.ical_uid} className="flex items-center gap-3 px-3 py-1.5 text-xs text-[#a0b2af]">
                        <Calendar size={12} />
                        <strong>{r.cleaning_date}</strong>
                        {r.summary && <span className="truncate">· {r.summary}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#1a4640] flex items-center justify-between gap-3">
          <div className="text-xs text-[#a0b2af]">
            {preview ? `${selected.size} selecionada(s) · pode reabrir e re-importar — duplicados são ignorados.`
                     : "Os dados só são criados depois de revistos abaixo."}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
                    className="px-4 py-2 text-sm text-[#a0b2af] hover:text-white"
                    data-testid="ical-cancel">
              Fechar
            </button>
            {preview && (
              <button onClick={doImport} disabled={importing || selected.size === 0}
                      className="btn-gold px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="ical-import-btn">
                {importing
                  ? <><Loader2 size={14} className="animate-spin" /> A importar…</>
                  : <>Criar {selected.size} limpeza{selected.size !== 1 ? "s" : ""}</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
